import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";

/**
 * MultiEmployeeSummaryTable - Displays totals per employee for multi-employee reports
 * Shows a breakdown of totals for each employee/driver in the report
 */
const MultiEmployeeSummaryTable = ({ data, reportConfig }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    return value.toLocaleString();
  };

  const modelName = reportConfig?.modelName || "";
  const employeeField = modelName === "Payout" ? "employee" : "driver";
  const dateField = modelName === "Payout" ? "payDate" : "dateCreated";

  // Group data by employee/driver and calculate totals per employee
  const employeeTotals = React.useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      // Get employee/driver ID and name
      const employee = item[employeeField];
      const employeeId = employee?._id?.toString() || employee?.toString() || "Unknown";
      const employeeName = employee?.fullName || employee?.name || employeeId;

      if (!grouped[employeeId]) {
        grouped[employeeId] = {
          employeeId,
          employeeName,
          count: 0,
          total: 0,
          totalSum: 0,
          mileageTotal: 0,
          hours: 0,
          supplementalPay: 0,
          mileagePay: 0,
        };
      }

      grouped[employeeId].count++;
      
      // Sum up numeric fields
      if (item.total !== undefined && item.total !== null) {
        grouped[employeeId].total += parseFloat(item.total) || 0;
        grouped[employeeId].totalSum += parseFloat(item.total) || 0;
      }
      if (item.mileageTotal !== undefined && item.mileageTotal !== null) {
        grouped[employeeId].mileageTotal += parseFloat(item.mileageTotal) || 0;
      }
      if (item.hours !== undefined && item.hours !== null) {
        grouped[employeeId].hours += parseFloat(item.hours) || 0;
      }
      if (item.supplementalPay !== undefined && item.supplementalPay !== null) {
        grouped[employeeId].supplementalPay += parseFloat(item.supplementalPay) || 0;
      }
      if (item.mileagePay !== undefined && item.mileagePay !== null) {
        grouped[employeeId].mileagePay += parseFloat(item.mileagePay) || 0;
      }
    });

    // Convert to array and sort by employee name
    return Object.values(grouped).sort((a, b) => 
      a.employeeName.localeCompare(b.employeeName)
    );
  }, [data, employeeField]);

  // Build table data
  const tableData = [];
  
  employeeTotals.forEach((employee) => {
    // Add employee header row
    tableData.push({
      type: "employee-header",
      employeeName: employee.employeeName,
      count: employee.count,
    });

    // Add totals for this employee
    if (employee.totalSum > 0) {
      tableData.push({
        type: "data",
        label: "Total",
        value: formatCurrency(employee.totalSum),
        isCurrency: true,
      });
    }
    if (employee.mileageTotal > 0) {
      tableData.push({
        type: "data",
        label: "Mileage Total",
        value: formatNumber(employee.mileageTotal),
        isCurrency: false,
      });
    }
    if (employee.hours > 0) {
      tableData.push({
        type: "data",
        label: "Hours",
        value: formatNumber(employee.hours),
        isCurrency: false,
      });
    }
    if (employee.supplementalPay > 0) {
      tableData.push({
        type: "data",
        label: "Supplemental Pay",
        value: formatCurrency(employee.supplementalPay),
        isCurrency: true,
      });
    }
    if (employee.mileagePay > 0) {
      tableData.push({
        type: "data",
        label: "Mileage Pay",
        value: formatCurrency(employee.mileagePay),
        isCurrency: true,
      });
    }

    // Add spacing row between employees
    tableData.push({ type: "spacer" });
  });

  // Remove last spacer
  if (tableData.length > 0 && tableData[tableData.length - 1].type === "spacer") {
    tableData.pop();
  }

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "label",
        header: "Employee / Metric",
        cell: ({ row }) => {
          const rowData = row.original;
          if (rowData.type === "employee-header") {
            return (
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "10px",
                  color: "#1f2937",
                }}
              >
                {rowData.employeeName} ({rowData.count} records)
              </span>
            );
          }
          if (rowData.type === "spacer") {
            return <span style={{ fontSize: "4px" }}>&nbsp;</span>;
          }
          return (
            <span
              style={{
                paddingLeft: "12px",
                fontSize: "9px",
              }}
            >
              {rowData.label}
            </span>
          );
        },
      },
      {
        accessorKey: "value",
        header: "Value",
        cell: ({ row }) => {
          const rowData = row.original;
          if (rowData.type === "employee-header" || rowData.type === "spacer") {
            return <span>&nbsp;</span>;
          }
          return (
            <span
              style={{
                fontSize: "9px",
                textAlign: "right",
                fontWeight: rowData.isCurrency ? "bold" : "normal",
              }}
            >
              {rowData.value || "-"}
            </span>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const thClasses = classNames(
    "border-b-2 py-2 text-xs text-left px-1 font-bold"
  );
  const tdClasses = classNames("py-1 text-sm text-left px-1");

  if (tableData.length === 0) {
    return null;
  }

  return (
    <table id="multi-employee-totals">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={thClasses}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          const rowData = row.original;
          const isEmployeeHeader = rowData.type === "employee-header";
          const isSpacer = rowData.type === "spacer";
          
          return (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className={tdClasses}
                    style={{
                      fontWeight: isEmployeeHeader ? "bold" : "normal",
                      backgroundColor: isEmployeeHeader ? "#e5e7eb" : "white",
                      paddingTop: isSpacer ? "4px" : "4px",
                      paddingBottom: isSpacer ? "4px" : "4px",
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MultiEmployeeSummaryTable;










